import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import ReactMarkdown from 'react-markdown';
import JobRecruiterTabs from '../components/JobRecruiterTabs';
import { isHmView, refreshStoredPermissions } from '../utils/permissions';
import ScreeningQuestionCard, { SkillChips } from '../components/ScreeningQuestionCard';

function RequirementResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requirementId } = useParams();
  const stateData = location.state?.resultData;
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [showDownloadModal, setShowDownloadModal] = useState(null);
  const [resultData, setResultData] = useState(stateData || null);
  const [isLoading, setIsLoading] = useState(!stateData && !!requirementId);
  const [fetchError, setFetchError] = useState('');

  // Detect if viewer is Hiring Manager from JWT token
  const [isHiringManager, setIsHiringManager] = useState(false);
  const [isEditingScreening, setIsEditingScreening] = useState(false);
  const [editedScreening, setEditedScreening] = useState('');
  const [isSavingScreening, setIsSavingScreening] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState('');
  // HM-created job (origin hm_jessica): approved JD + recruiter brief for the recruiting team
  const [recruiterView, setRecruiterView] = useState(null);
  const [recruiterViewError, setRecruiterViewError] = useState('');

  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem('user') || 'null'); } catch {}
    setIsHiringManager(isHmView(stored));
    refreshStoredPermissions().then((u) => { if (u) setIsHiringManager(isHmView(u)); });
  }, []);

  // Fetch from API when navigating via View button (no state data, has requirementId)
  useEffect(() => {
    if (stateData || !requirementId) return;
    const fetchRequirement = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/'); return; }
      try {
        setIsLoading(true);
        const response = await fetch(API_ENDPOINTS.REQUIREMENTS_VIEW(requirementId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setResultData(result.data);
        } else if (response.status === 401) {
          localStorage.clear(); navigate('/');
        } else if (response.status === 403) {
          setFetchError('You do not have permission to view this requirement.');
        } else {
          setFetchError('Failed to load requirement details.');
        }
      } catch (error) {
        console.error('Error fetching requirement:', error);
        setFetchError('Connection error. Please check if the backend is running.');
      } finally { setIsLoading(false); }
    };
    fetchRequirement();
  }, [requirementId, stateData, navigate]);

  useEffect(() => {
    if (!resultData || resultData.origin !== 'hm_jessica' || isHiringManager || recruiterView) return;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${API_BASE}/api/job-routing/requirements/${resultData.id}/recruiter-view`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (res.ok) setRecruiterView(data); else setRecruiterViewError(data.detail || 'Not available.');
    }).catch(() => setRecruiterViewError('Connection error. Please try again.'));
  }, [resultData, isHiringManager, recruiterView]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-blue border-t-transparent mx-auto"></div>
          <p className="text-gray-600 mt-6 font-medium text-lg">Loading requirement details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-12 max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-600 mb-6">{fetchError}</p>
          <button onClick={() => navigate(-1)} className="px-6 py-3 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 font-semibold">
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // If no data at all, go back
  if (!resultData) {
    navigate(-1);
    return null;
  }

  const {
    job_title,
    req_id,
    quality_score,
    extracted_data,
    missing_alerts,
    recruiter_document,
    screening_questions,
    is_duplicate,
    original_date,
    company_usage,
    approved_by_hiring_manager,
    approved_at
  } = resultData;

  // Quality score color coding
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 60) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return '✅';
    if (score >= 60) return '⚠️';
    return '❌';
  };

  // ─── Screening Questions Parser ───────────────────────────────────
  const parseScreeningQuestions = (markdown) => {
    if (!markdown) return [];
    // Split by **Question N: ... ** pattern
    const blocks = markdown.split(/(?=\*\*Question\s+\d+)/i).filter(b => b.trim());
    return blocks.map((block, index) => {
      // Category from title line
      const titleMatch = block.match(/\*\*Question\s+\d+[:\s]*[–-]?\s*(.+?)\*\*/i);
      const category = titleMatch ? titleMatch[1].replace(/^[-–—:\s]+/, '').trim() : `Question ${index + 1}`;
      // Q: line
      const qMatch = block.match(/(?:^|\n)\s*Q:\s*(.+)/i);
      const question = qMatch ? qMatch[1].trim() : '';
      // What to Listen For (stop at Red Flag, Follow-up, ---, or next **Question)
      const listenMatch = block.match(/(?:What to Listen For|Listen For|Expected Answer)[:\s]*(.+?)(?=\n\s*(?:Red Flag|Follow[- ]?up|---|\*\*Question)|$)/is);
      const listenFor = listenMatch ? listenMatch[1].trim() : '';
      // Red Flags
      const redFlagMatch = block.match(/Red Flag[s]?[:\s]*(.+?)(?=\n\s*(?:Follow[- ]?up|---|\*\*Question)|$)/is);
      const redFlags = redFlagMatch ? redFlagMatch[1].trim() : '';
      // Follow-up
      const followUpMatch = block.match(/Follow[- ]?up[:\s]*(.+?)(?=\n\s*(?:---|\*\*Question)|$)/is);
      const followUp = followUpMatch ? followUpMatch[1].trim() : '';
      // Detect type for color coding
      const cl = category.toLowerCase();
      let type = 'general';
      if (cl.includes('technical') || cl.includes('skill') || cl.includes('coding') || cl.includes('programming')) type = 'technical';
      else if (cl.includes('experience') || cl.includes('background')) type = 'experience';
      else if (cl.includes('behavioral') || cl.includes('situational') || cl.includes('problem')) type = 'behavioral';
      else if (cl.includes('culture') || cl.includes('fit') || cl.includes('motivation') || cl.includes('teamwork')) type = 'culture';
      return { category, question, listenFor, redFlags, followUp, type };
    }).filter(q => q.question);
  };

  const categoryStyles = {
    technical:  { badge: 'bg-blue-100 text-blue-800 border-blue-200', bar: 'bg-blue-500', icon: '💻', ring: 'ring-blue-200' },
    experience: { badge: 'bg-purple-100 text-purple-800 border-purple-200', bar: 'bg-purple-500', icon: '📈', ring: 'ring-purple-200' },
    behavioral: { badge: 'bg-amber-100 text-amber-800 border-amber-200', bar: 'bg-amber-500', icon: '🎯', ring: 'ring-amber-200' },
    culture:    { badge: 'bg-teal-100 text-teal-800 border-teal-200', bar: 'bg-teal-500', icon: '🤝', ring: 'ring-teal-200' },
    general:    { badge: 'bg-gray-100 text-gray-700 border-gray-200', bar: 'bg-gray-500', icon: '❓', ring: 'ring-gray-200' }
  };
  const getCS = (type) => categoryStyles[type] || categoryStyles.general;

  // ─── Recruiter Doc Section Styles ─────────────────────────────────
  const getSectionStyle = (heading) => {
    const h = heading.toLowerCase();
    if (h.includes('role') || h.includes('summary') || h.includes('overview') || h.includes('position'))
      return { bar: 'bg-blue-500', badge: 'bg-blue-50 border-blue-200 text-blue-800', icon: '📌', accent: 'border-l-blue-500', bg: 'bg-blue-50' };
    if (h.includes('must') || h.includes('required') || h.includes('qualification') || h.includes('critical'))
      return { bar: 'bg-red-500', badge: 'bg-red-50 border-red-200 text-red-800', icon: '⭐', accent: 'border-l-red-500', bg: 'bg-red-50' };
    if (h.includes('nice') || h.includes('preferred') || h.includes('bonus') || h.includes('optional'))
      return { bar: 'bg-purple-500', badge: 'bg-purple-50 border-purple-200 text-purple-800', icon: '✨', accent: 'border-l-purple-500', bg: 'bg-purple-50' };
    if (h.includes('compens') || h.includes('salary') || h.includes('pay') || h.includes('benefit'))
      return { bar: 'bg-green-500', badge: 'bg-green-50 border-green-200 text-green-800', icon: '💰', accent: 'border-l-green-500', bg: 'bg-green-50' };
    if (h.includes('red flag') || h.includes('knockout') || h.includes('disqualif') || h.includes('avoid'))
      return { bar: 'bg-rose-600', badge: 'bg-rose-50 border-rose-200 text-rose-800', icon: '🚩', accent: 'border-l-rose-600', bg: 'bg-rose-50' };
    if (h.includes('interview') || h.includes('process') || h.includes('stage') || h.includes('hiring'))
      return { bar: 'bg-amber-500', badge: 'bg-amber-50 border-amber-200 text-amber-800', icon: '🤝', accent: 'border-l-amber-500', bg: 'bg-amber-50' };
    if (h.includes('messaging') || h.includes('pitch') || h.includes('selling') || h.includes('sell') || h.includes('value'))
      return { bar: 'bg-teal-500', badge: 'bg-teal-50 border-teal-200 text-teal-800', icon: '💬', accent: 'border-l-teal-500', bg: 'bg-teal-50' };
    if (h.includes('location') || h.includes('remote') || h.includes('work model') || h.includes('onsite'))
      return { bar: 'bg-indigo-500', badge: 'bg-indigo-50 border-indigo-200 text-indigo-800', icon: '📍', accent: 'border-l-indigo-500', bg: 'bg-indigo-50' };
    if (h.includes('responsib') || h.includes('duties') || h.includes('accountab'))
      return { bar: 'bg-cyan-500', badge: 'bg-cyan-50 border-cyan-200 text-cyan-800', icon: '🎯', accent: 'border-l-cyan-500', bg: 'bg-cyan-50' };
    return { bar: 'bg-gray-400', badge: 'bg-gray-50 border-gray-200 text-gray-700', icon: '📄', accent: 'border-l-gray-400', bg: 'bg-gray-50' };
  };

  // ─── Custom Markdown Renderer (no prose plugin needed) ─────────────
  const renderMarkdownContent = (markdown) => {
    if (!markdown) return null;
    const lines = markdown.split('\n');
    const elements = [];
    let listItems = [];
    let key = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={key++} className="my-3 space-y-1.5 pl-1">
            {listItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-gray-700 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></span>
                <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-900 font-semibold">$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>') }} />
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }
    };

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) { flushList(); elements.push(<div key={key++} className="h-2" />); return; }

      if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(<h4 key={key++} className="text-sm font-bold text-gray-800 mt-4 mb-2 uppercase tracking-wide">{trimmed.slice(4)}</h4>);
      } else if (trimmed.startsWith('#### ')) {
        flushList();
        elements.push(<h5 key={key++} className="text-sm font-semibold text-gray-700 mt-3 mb-1">{trimmed.slice(5)}</h5>);
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.match(/^\d+\.\s/)) {
        listItems.push(trimmed.replace(/^[-•]\s+/, '').replace(/^\d+\.\s+/, ''));
      } else if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
        flushList();
        elements.push(<p key={key++} className="font-bold text-gray-900 text-sm mt-3 mb-1">{trimmed.replace(/\*\*/g, '')}</p>);
      } else {
        flushList();
        const html = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
        elements.push(<p key={key++} className="text-gray-700 text-sm leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: html }} />);
      }
    });
    flushList();
    return elements;
  };

  const parsedQuestions = parseScreeningQuestions(screening_questions);
  // HM-created jobs (Jessica): technical knowledge checks shown with the shared card (no follow-up)
  const isHmJessicaJob = resultData?.origin === 'hm_jessica';
  const hmQuestions = parsedQuestions.map((q) => ({ skill: q.category.replace(/^Technical Skills\s*[–-]\s*/i, ''),
    question: q.question, expected_answer: q.listenFor, red_flags: q.redFlags }));

  // Convert markdown to HTML for better formatting
  const markdownToHtml = (markdown) => {
    return markdown
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');
  };

  // Download functions
  const downloadDocument = async (format) => {
    const fileName = `${job_title.replace(/[^a-z0-9]/gi, '_')}_Recruiter_Guide`;
    
    if (format === 'pdf') {
      // PDF Download using jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Split text into lines to fit PDF width
      const lines = doc.splitTextToSize(recruiter_document, 180);
      let y = 20;
      
      lines.forEach((line) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 15, y);
        y += 7;
      });
      
      doc.save(`${fileName}.pdf`);
    } else if (format === 'docx') {
      // DOCX Download
      const htmlDocx = await import('html-docx-js/dist/html-docx');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; }
            h1 { font-size: 24px; font-weight: bold; margin-top: 20px; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 16px; color: #0066CC; }
            h3 { font-size: 16px; font-weight: bold; margin-top: 12px; }
            p { margin: 8px 0; }
            strong { font-weight: bold; }
          </style>
        </head>
        <body>
          ${markdownToHtml(recruiter_document)}
        </body>
        </html>
      `;
      
      const blob = htmlDocx.asBlob(htmlContent);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
    
    setShowDownloadModal(null);
  };

  const downloadQuestions = async (format) => {
    const fileName = `${job_title.replace(/[^a-z0-9]/gi, '_')}_Screening_Questions`;
    
    if (format === 'pdf') {
      // PDF Download
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      const lines = doc.splitTextToSize(screening_questions, 180);
      let y = 20;
      
      lines.forEach((line) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 15, y);
        y += 7;
      });
      
      doc.save(`${fileName}.pdf`);
    } else if (format === 'docx') {
      // DOCX Download
      const htmlDocx = await import('html-docx-js/dist/html-docx');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; }
            h1 { font-size: 24px; font-weight: bold; margin-top: 20px; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 16px; color: #10B981; }
            h3 { font-size: 16px; font-weight: bold; margin-top: 12px; }
            p { margin: 8px 0; }
            strong { font-weight: bold; }
          </style>
        </head>
        <body>
          ${markdownToHtml(screening_questions)}
        </body>
        </html>
      `;
      
      const blob = htmlDocx.asBlob(htmlContent);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
    
    setShowDownloadModal(null);
  };

  const downloadAll = () => {
    setShowDownloadModal('both');
  };

  // Extract key data points
  const compensation = extracted_data?.compensation;
  const location_data = extracted_data?.location_and_work_model;
  const experience = extracted_data?.must_have_requirements?.min_years_experience;

  // HM: save edited screening questions
  const handleSaveScreening = async () => {
    setIsSavingScreening(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/requirements/${resultData.id}/update-screening`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ screening_questions: editedScreening, meeting_id: resultData.meeting_id || null })
      });
      if (res.ok) {
        setResultData(prev => ({ ...prev, screening_questions: editedScreening }));
        setIsEditingScreening(false);
      } else { alert('Failed to save. Please try again.'); }
    } catch { alert('Failed to save. Please try again.'); }
    finally { setIsSavingScreening(false); }
  };

  // HM: approve screening questions
  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/requirements/${resultData.id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ screening_questions: resultData.screening_questions, meeting_id: resultData.meeting_id || null })
      });
      if (res.ok) {
        setResultData(prev => ({ ...prev, approved_by_hiring_manager: true, approved_at: new Date().toISOString() }));
        setApproveSuccess(true);
      } else { alert('Failed to approve. Please try again.'); }
    } catch { alert('Failed to approve. Please try again.'); }
    finally { setIsApproving(false); }
  };

  // Fetch intake transcript from Phase 2 meeting
  const fetchTranscript = async () => {
    if (transcriptData || isLoadingTranscript) return;
    if (resultData?.origin === 'hm_jessica') {          // Jessica call with the HM (no scheduled meeting)
      if (recruiterView && recruiterView.transcript) setTranscriptData({ transcript: recruiterView.transcript, hiring_manager: resultData.hiring_manager_name });
      else setTranscriptError(recruiterViewError || 'No call transcript for this job.');
      return;
    }
    if (!resultData?.meeting_id) { setTranscriptError('No intake call linked to this requirement.'); return; }
    setIsLoadingTranscript(true);
    setTranscriptError('');
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${API_BASE}/api/meetings/${resultData.meeting_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        const meeting = json.data || json;
        setTranscriptData(meeting);
      } else if (res.status === 404) {
        setTranscriptError('Intake call record not found.');
      } else {
        setTranscriptError('Failed to load transcript. Please try again.');
      }
    } catch {
      setTranscriptError('Connection error. Please try again.');
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  // Parse transcript text into turns [{speaker, text}]
  const parseTranscriptTurns = (raw) => {
    if (!raw || typeof raw !== 'string') return [];
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const turns = [];
    let current = null;
    lines.forEach(line => {
      // Match "Jessica: ..." or "Agent: ..." or "User: ..." or "Hiring Manager: ..."
      const match = line.match(/^([A-Za-z][A-Za-z\s]{0,30}):\s*(.+)/);
      if (match) {
        if (current) turns.push(current);
        const speakerRaw = match[1].trim();
        const isAI = /jessica|agent|ai|assistant/i.test(speakerRaw);
        current = { speaker: speakerRaw, isAI, text: match[2].trim() };
      } else if (current) {
        current.text += ' ' + line;
      } else {
        turns.push({ speaker: 'System', isAI: false, isSystem: true, text: line });
      }
    });
    if (current) turns.push(current);
    return turns;
  };

  // Tab configuration — HM only sees Overview + Screening Questions
  const allTabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'recruiter-doc', label: 'Recruiter Guide', icon: '📋' },
    { id: 'screening', label: 'Screening Questions', icon: '❓' },
    { id: 'intake-transcript', label: 'Intake Transcript', icon: '🎙️' },
    { id: 'extracted-data', label: 'Extracted Data', icon: '📑' }
  ];
  if (resultData?.origin === 'hm_jessica') {
    allTabs.splice(1, 0, { id: 'job-description', label: 'Job Description', icon: '📄' },
                         { id: 'recruiter-brief', label: 'Recruiter Brief', icon: '🎯' });
  }
  const tabs = isHiringManager
    ? allTabs.filter(t => t.id === 'overview' || t.id === 'screening')
    : allTabs;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Original Header - No Changes */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Reqt5d-Logo.png" alt="Reqt5d" className="h-14" />
            <div>
              <p className="text-sm text-gray-600">Requirement Results</p>
              <p className="text-lg font-semibold text-gray-900" data-testid="results-title">{req_id ? `${req_id} – ` : ''}{job_title}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Status Banner - Only show when coming from processing flow */}
        {stateData && is_duplicate && (
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">♻️</span>
              <div>
                <h3 className="font-bold text-blue-900">Cached Results Retrieved</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Previously analyzed on {new Date(original_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. 
                  Cached results returned to save quota.
                </p>
              </div>
            </div>
          </div>
        )}

        {stateData && !is_duplicate && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <h3 className="font-bold text-green-900">Analysis Completed!</h3>
                <p className="text-sm text-green-700 mt-1">
                  Your job requirement has been successfully analyzed. All documents and insights are ready for review.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs Navigation - IN BODY SECTION */}
        <div className="bg-white rounded-t-lg shadow-sm border border-gray-200 border-b-0">
          <div className="flex border-b border-gray-200">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'intake-transcript') fetchTranscript();
                }}
                className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-brand-blue bg-blue-50 border-b-2 border-brand-blue'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                } ${index !== 0 ? 'border-l border-gray-200' : ''}`}
              >
                <span className="mr-2 text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Requirement Overview</h2>

              {/* Quality Score - Compact in Body */}
              <div className="mb-8">
                <div className={`inline-flex items-center gap-4 px-6 py-4 rounded-xl border-2 ${getScoreBg(quality_score)}`}>
                  <span className="text-3xl">{getScoreIcon(quality_score)}</span>
                  <div>
                    <div className={`text-4xl font-bold ${getScoreColor(quality_score)}`}>{quality_score}</div>
                    <div className="text-sm text-gray-600 font-medium">Quality Score</div>
                  </div>
                  <div className="border-l-2 border-gray-300 pl-4 ml-2">
                    <p className="text-sm text-gray-700">
                      {quality_score >= 80 && "Excellent quality"}
                      {quality_score >= 60 && quality_score < 80 && "Good quality"}
                      {quality_score < 60 && "Needs improvement"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Compensation */}
                <div className="border border-gray-200 rounded-xl p-6 hover:border-brand-blue transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <span className="text-2xl">💰</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Compensation</h3>
                  </div>
                  {compensation?.pay_range_min && compensation?.pay_range_max ? (
                    <p className="text-xl font-bold text-green-600">
                      ${(compensation.pay_range_min / 1000).toFixed(0)}K - ${(compensation.pay_range_max / 1000).toFixed(0)}K
                    </p>
                  ) : (
                    <p className="text-base text-gray-500 font-medium">Not specified</p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">{compensation?.pay_type || 'Salary'}</p>
                </div>

                {/* Location */}
                <div className="border border-gray-200 rounded-xl p-6 hover:border-brand-blue transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <span className="text-2xl">📍</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Work Model</h3>
                  </div>
                  <p className="text-xl font-bold text-blue-600">
                    {location_data?.work_model || 'Not specified'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{location_data?.job_location || 'Location TBD'}</p>
                </div>

                {/* Experience */}
                <div className="border border-gray-200 rounded-xl p-6 hover:border-brand-blue transition-colors">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <span className="text-2xl">⭐</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Experience</h3>
                  </div>
                  {experience ? (
                    <p className="text-xl font-bold text-purple-600">{experience}+ Years</p>
                  ) : (
                    <p className="text-base text-gray-500 font-medium">Not specified</p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Minimum required</p>
                </div>
              </div>

              {/* Missing Alerts */}
              {missing_alerts && missing_alerts.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">⚠️</span>
                    <h3 className="text-xl font-bold text-gray-900">Missing Information ({missing_alerts.length})</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {missing_alerts.map((alert, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <span className="text-yellow-600 font-bold">•</span>
                        <p className="text-sm text-gray-800 font-medium">{alert}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                <div className="flex gap-3">
                  <button
                    onClick={downloadAll}
                    className="px-6 py-3 bg-gradient-to-r from-brand-green to-teal-600 text-white rounded-lg hover:shadow-lg font-semibold"
                  >
                    📥 Download All Documents
                  </button>
                  <button
                    onClick={() => setActiveTab('recruiter-doc')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  >
                    📋 View Recruiter Guide
                  </button>
                  <button
                    onClick={() => setActiveTab('screening')}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  >
                    ❓ View Questions
                  </button>
                </div>
              </div>

              {/* Usage Info */}
              {company_usage && (
              <div className="border-t border-gray-200 mt-6 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Subscription Usage</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {company_usage.requirements_used ?? company_usage.current_usage ?? 0} of {company_usage.monthly_limit ?? 0} requirements used this month
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-brand-blue">
                      {(company_usage.monthly_limit ?? 0) - (company_usage.requirements_used ?? company_usage.current_usage ?? 0)}
                    </div>
                    <div className="text-xs text-gray-600">Remaining</div>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {/* Recruiter Document Tab */}
          {activeTab === 'recruiter-doc' && (
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                    <span className="text-2xl">📋</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Recruiter Guide</h2>
                    <p className="text-sm text-gray-500 mt-0.5">AI-generated brief for sourcing & screening — {(recruiter_document || '').split(/(?=^##\s)/m).filter(s => s.trim()).length} sections</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const sections = (recruiter_document || '').split(/(?=^##\s)/m).filter(s => s.trim());
                      const allExpanded = {};
                      sections.forEach((_, index) => { allExpanded[index] = true; });
                      setExpandedSections(allExpanded);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors"
                  >
                    Expand All
                  </button>
                  <button
                    onClick={() => setExpandedSections({})}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors"
                  >
                    Collapse All
                  </button>
                  <button
                    onClick={() => setShowDownloadModal('recruiter')}
                    className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg font-semibold text-sm transition-all"
                  >
                    📥 Download
                  </button>
                </div>
              </div>

              {/* Section cards */}
              <div className="space-y-3">
                {(recruiter_document || '').split(/(?=^##\s)/m).filter(s => s.trim()).map((section, index) => {
                  const lines = section.split('\n');
                  const heading = lines[0].replace(/^##\s+/, '').trim();
                  const content = lines.slice(1).join('\n').trim();
                  const isExpanded = expandedSections[index];
                  const style = getSectionStyle(heading);

                  return (
                    <div key={index} className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      {/* Coloured top bar */}
                      <div className={`h-1 ${style.bar}`} />

                      {/* Section header button */}
                      <button
                        onClick={() => setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl ${style.bar} flex items-center justify-center text-white text-lg shadow-sm`}>
                            {style.icon}
                          </div>
                          <span className="text-base font-bold text-gray-900">{heading}</span>
                          <span className={`hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${style.badge}`}>
                            {isExpanded ? 'Expanded' : 'Collapsed'}
                          </span>
                        </div>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform ${isExpanded ? 'bg-gray-200 rotate-180' : 'bg-gray-100'}`}>
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>

                      {/* Expanded content */}
                      {isExpanded && content && (
                        <div className={`border-t border-gray-100`}>
                          <div className={`border-l-4 ${style.accent} mx-6 my-5 pl-4 ${style.bg} rounded-r-xl py-4 pr-4`}>
                            {renderMarkdownContent(content)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Screening Questions Tab */}
          {activeTab === 'screening' && (
            <div className="p-8">
              {/* ── Header + Approval Badge ── */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">❓</span>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Screening Questions</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {parsedQuestions.length} questions for Level 1 candidate screening
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {approved_by_hiring_manager && (
                    <div className="flex items-center gap-2.5 px-5 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm">
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow">
                        <span className="text-white text-sm font-bold">✓</span>
                      </div>
                      <div className="leading-tight">
                        <p className="text-green-800 font-bold text-sm">Approved by Hiring Manager</p>
                        {approved_at && (
                          <p className="text-green-600 text-xs">
                            {new Date(approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowDownloadModal('screening')}
                    className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:shadow-lg font-semibold transition-all"
                  >
                    📥 Download
                  </button>
                  {/* ── HM Edit + Approve buttons ── */}
                  {isHiringManager && !approved_by_hiring_manager && !approveSuccess && (
                    <>
                      <button
                        onClick={() => { setIsEditingScreening(!isEditingScreening); setEditedScreening(resultData.screening_questions || ''); }}
                        className="px-5 py-2.5 bg-amber-50 text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 font-semibold transition-all text-sm"
                      >
                        ✏️ {isEditingScreening ? 'Cancel Edit' : 'Edit'}
                      </button>
                      <button
                        onClick={handleApprove}
                        disabled={isApproving || isEditingScreening}
                        className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-all text-sm disabled:opacity-50"
                      >
                        {isApproving ? 'Approving...' : '✅ Approve'}
                      </button>
                    </>
                  )}
                  {(approved_by_hiring_manager || approveSuccess) && isHiringManager && (
                    <span className="text-green-700 font-bold text-sm flex items-center gap-1">✅ Approved</span>
                  )}
                </div>
              </div>

              {/* ── Category Legend (HM-created jobs: questions per skill) ── */}
              {parsedQuestions.length > 0 && isHmJessicaJob && <div className="mb-6"><SkillChips questions={hmQuestions} /></div>}
              {parsedQuestions.length > 0 && !isHmJessicaJob && (
                <div className="flex flex-wrap gap-2.5 mb-6">
                  {['technical', 'experience', 'behavioral', 'culture'].map(type => {
                    const count = parsedQuestions.filter(q => q.type === type).length;
                    if (!count) return null;
                    const s = getCS(type);
                    return (
                      <span key={type} className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border ${s.badge}`}>
                        {s.icon} {type.charAt(0).toUpperCase() + type.slice(1)} · {count}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* ── HM Inline Editor ── */}
              {isHiringManager && isEditingScreening && (
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <p className="text-sm font-bold text-amber-700 mb-3">✏️ Edit Screening Questions</p>
                  <textarea
                    value={editedScreening}
                    onChange={e => setEditedScreening(e.target.value)}
                    rows={16}
                    className="w-full px-4 py-3 border border-amber-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white"
                  />
                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={handleSaveScreening}
                      disabled={isSavingScreening}
                      className="px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-50"
                    >
                      {isSavingScreening ? 'Saving...' : '💾 Save Changes'}
                    </button>
                    <button
                      onClick={() => setIsEditingScreening(false)}
                      className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Question Cards ── */}
              {parsedQuestions.length > 0 && isHmJessicaJob ? (
                <div className="space-y-5">
                  {hmQuestions.map((q, idx) => <ScreeningQuestionCard key={idx} q={q} index={idx} />)}
                </div>
              ) : parsedQuestions.length > 0 ? (
                <div className="space-y-5">
                  {parsedQuestions.map((q, idx) => {
                    const s = getCS(q.type);
                    return (
                      <div key={idx} className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                        {/* Color bar at top */}
                        <div className={`h-1.5 ${s.bar}`} />

                        {/* Card header */}
                        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${s.bar} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                              {idx + 1}
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${s.badge}`}>
                              {s.icon} {q.category}
                            </span>
                          </div>
                        </div>

                        {/* The question */}
                        <div className="px-6 pb-4">
                          <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-brand-blue flex items-center justify-center mt-0.5">
                              <span className="text-white font-bold text-xs">Q</span>
                            </div>
                            <p className="text-gray-900 font-semibold text-base leading-relaxed">{q.question}</p>
                          </div>
                        </div>

                        {/* Answer guidance grid */}
                        <div className="px-6 pb-5">
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* What to Listen For */}
                            {q.listenFor && (
                              <div className="rounded-xl p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100">
                                <div className="flex items-center gap-2 mb-2.5">
                                  <div className="w-6 h-6 rounded-md bg-green-500 flex items-center justify-center">
                                    <span className="text-white text-xs">👂</span>
                                  </div>
                                  <h4 className="text-sm font-bold text-green-800">What to Listen For</h4>
                                </div>
                                <div className="text-sm text-green-700 leading-relaxed space-y-1.5">
                                  {q.listenFor.split('\n').filter(l => l.trim()).map((line, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                      {line.trim().startsWith('•') || line.trim().startsWith('-') ? (
                                        <>
                                          <span className="text-green-400 mt-1 flex-shrink-0">●</span>
                                          <span>{line.replace(/^[•-]\s*/, '')}</span>
                                        </>
                                      ) : (
                                        <span>{line}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Red Flags */}
                            {q.redFlags && (
                              <div className="rounded-xl p-4 bg-gradient-to-br from-red-50 to-rose-50 border border-red-100">
                                <div className="flex items-center gap-2 mb-2.5">
                                  <div className="w-6 h-6 rounded-md bg-red-500 flex items-center justify-center">
                                    <span className="text-white text-xs">🚩</span>
                                  </div>
                                  <h4 className="text-sm font-bold text-red-800">Red Flags</h4>
                                </div>
                                <div className="text-sm text-red-700 leading-relaxed space-y-1.5">
                                  {q.redFlags.split('\n').filter(l => l.trim()).map((line, i) => (
                                    <div key={i} className="flex items-start gap-2">
                                      {line.trim().startsWith('•') || line.trim().startsWith('-') ? (
                                        <>
                                          <span className="text-red-400 mt-1 flex-shrink-0">●</span>
                                          <span>{line.replace(/^[•-]\s*/, '')}</span>
                                        </>
                                      ) : (
                                        <span>{line}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Follow-up */}
                          {q.followUp && (
                            <div className="mt-4 rounded-xl p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className="w-6 h-6 rounded-md bg-blue-500 flex items-center justify-center">
                                  <span className="text-white text-xs">🔄</span>
                                </div>
                                <h4 className="text-sm font-bold text-blue-800">Follow-up Question</h4>
                              </div>
                              <p className="text-sm text-blue-700 leading-relaxed italic ml-8">{q.followUp}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Fallback: render raw markdown if parsing fails */
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="p-8 prose prose-lg max-w-none
                    prose-headings:text-gray-900
                    prose-h2:text-xl prose-h2:font-bold prose-h2:mb-3 prose-h2:mt-6 prose-h2:text-green-700
                    prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-3
                    prose-strong:text-gray-900 prose-strong:font-bold
                    prose-ul:my-3 prose-ul:list-disc prose-ul:pl-6
                    prose-li:text-gray-700 prose-li:mb-2">
                    <ReactMarkdown>{screening_questions || 'No screening questions available.'}</ReactMarkdown>
                  </div>
                </div>
              )}

              {/* ── Summary Footer ── */}
              <div className="mt-6 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5 text-sm text-gray-600">
                    <span className="font-bold text-gray-800">📊 {parsedQuestions.length} Questions</span>
                    <span className="text-gray-300">|</span>
                    {['technical', 'experience', 'behavioral', 'culture'].map(type => {
                      const count = parsedQuestions.filter(q => q.type === type).length;
                      if (!count) return null;
                      const s = getCS(type);
                      return <span key={type}>{s.icon} {count} {type.charAt(0).toUpperCase() + type.slice(1)}</span>;
                    })}
                  </div>
                  {approved_by_hiring_manager && (
                    <span className="text-green-700 font-bold text-sm flex items-center gap-1.5">
                      ✅ HM Approved
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'job-description' && <JobRecruiterTabs tab="jd" view={recruiterView} error={recruiterViewError} />}
          {activeTab === 'recruiter-brief' && <JobRecruiterTabs tab="brief" view={recruiterView} error={recruiterViewError} />}

          {/* Intake Transcript Tab */}
          {activeTab === 'intake-transcript' && (
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shadow-lg">
                    <span className="text-2xl">🎙️</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Intake Call Transcript</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Full conversation between Jessica (AI) and the Hiring Manager</p>
                  </div>
                </div>
                {transcriptData?.transcript && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-semibold text-green-700">Transcript Available</span>
                  </div>
                )}
              </div>

              {/* Loading */}
              {isLoadingTranscript && (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent"></div>
                  <p className="text-gray-500 font-medium">Loading intake transcript...</p>
                </div>
              )}

              {/* Error */}
              {transcriptError && !isLoadingTranscript && (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-3xl">🎙️</div>
                  <p className="text-gray-700 font-semibold text-lg">No Transcript Available</p>
                  <p className="text-gray-400 text-sm text-center max-w-md">{transcriptError}</p>
                  <p className="text-gray-400 text-xs text-center max-w-sm">Transcripts are only available for requirements created via an AI intake call (Phase 2). JD-upload requirements do not have a transcript.</p>
                </div>
              )}

              {/* Transcript content */}
              {!isLoadingTranscript && !transcriptError && transcriptData && (
                <>
                  {/* Meeting metadata banner */}
                  <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Hiring Manager', value: transcriptData.hiring_manager || transcriptData.hiring_manager_name || transcriptData.hm_name || '—', icon: '👤' },
                      { label: 'Date', value: transcriptData.scheduled_date ? new Date(transcriptData.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—', icon: '📅' },
                      { label: 'Duration', value: transcriptData.duration_minutes ? `${transcriptData.duration_minutes} min` : '—', icon: '⏱️' },
                      { label: 'Status', value: transcriptData.meeting_status || transcriptData.status || '—', icon: '✅' },
                    ].map((item, i) => (
                      <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
                        <span className="text-lg">{item.icon}</span>
                        <div>
                          <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                          <p className="text-sm font-bold text-gray-800 capitalize">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* No transcript text */}
                  {!transcriptData.transcript && (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <div className="w-16 h-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-3xl">⏳</div>
                      <p className="text-gray-700 font-semibold">Transcript Not Yet Available</p>
                      <p className="text-gray-400 text-sm text-center max-w-md">The intake call is either still in progress or the transcript is being processed. Check back shortly after the call ends.</p>
                    </div>
                  )}

                  {/* Chat bubble transcript */}
                  {transcriptData.transcript && (() => {
                    const turns = parseTranscriptTurns(transcriptData.transcript);
                    if (turns.length === 0) return (
                      <div className="text-center py-10 text-gray-400">Transcript is empty or could not be parsed.</div>
                    );
                    return (
                      <div className="space-y-1">
                        {/* Legend */}
                        <div className="flex items-center gap-5 mb-5 pb-4 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                              <span className="text-white text-xs font-bold">AI</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-700">Jessica (AI Agent)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                              <span className="text-white text-xs font-bold">HM</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-700">Hiring Manager</span>
                          </div>
                          <span className="ml-auto text-xs text-gray-400 font-medium">{turns.length} turns</span>
                        </div>

                        {/* Turns */}
                        {turns.map((turn, idx) => {
                          if (turn.isSystem) return (
                            <div key={idx} className="text-center py-2">
                              <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{turn.text}</span>
                            </div>
                          );
                          return (
                            <div key={idx} className={`flex items-start gap-3 py-2 ${turn.isAI ? '' : 'flex-row-reverse'}`}>
                              {/* Avatar */}
                              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm ${turn.isAI ? 'bg-gradient-to-br from-violet-500 to-purple-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
                                <span className="text-white text-xs font-bold">{turn.isAI ? 'AI' : 'HM'}</span>
                              </div>
                              {/* Bubble */}
                              <div className={`max-w-[72%] ${turn.isAI ? '' : ''}`}>
                                <p className={`text-xs font-semibold mb-1 ${turn.isAI ? 'text-violet-600' : 'text-blue-600 text-right'}`}>{turn.speaker}</p>
                                <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
                                  turn.isAI
                                    ? 'bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 text-gray-800 rounded-tl-sm'
                                    : 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 text-gray-800 rounded-tr-sm'
                                }`}>
                                  {turn.text}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Extracted Data Tab */}
          {activeTab === 'extracted-data' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-3xl">📑</span>
                Extracted Data Overview
              </h2>
              
              <p className="text-gray-600 mb-6">
                AI has analyzed and extracted information across 11 key categories. Green cards indicate complete data, gray cards show incomplete sections.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(extracted_data || {}).map(([key, value]) => {
                  const icons = {
                    job_basics: '📌',
                    compensation: '💰',
                    location_and_work_model: '📍',
                    employment_and_legal: '⚖️',
                    core_responsibilities: '🎯',
                    must_have_requirements: '⭐',
                    nice_to_have_skills: '✨',
                    interview_process: '🤝',
                    screening_knockout_questions: '❓',
                    onboarding_start_date: '📅',
                    company_and_role_messaging: '💼'
                  };
                  
                  const hasData = value && Object.values(value).some(v => 
                    v !== null && v !== undefined && v !== '' && 
                    (Array.isArray(v) ? v.length > 0 : true)
                  );

                  return (
                    <div 
                      key={key} 
                      className={`p-4 rounded-xl border-2 transition-all ${
                        hasData 
                          ? 'bg-green-50 border-green-200 hover:border-green-300 hover:shadow-md' 
                          : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{icons[key] || '📄'}</div>
                      <p className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className={`text-xs font-semibold mt-1 ${hasData ? 'text-green-600' : 'text-gray-400'}`}>
                        {hasData ? '✓ Complete' : '○ Incomplete'}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Summary Stats */}
              <div className="mt-8 grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {Object.entries(extracted_data || {}).filter(([_, value]) => 
                      value && Object.values(value).some(v => 
                        v !== null && v !== undefined && v !== '' && 
                        (Array.isArray(v) ? v.length > 0 : true)
                      )
                    ).length}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Categories Complete</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-gray-600">
                    {Object.entries(extracted_data || {}).filter(([_, value]) => 
                      !value || !Object.values(value).some(v => 
                        v !== null && v !== undefined && v !== '' && 
                        (Array.isArray(v) ? v.length > 0 : true)
                      )
                    ).length}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Categories Incomplete</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">11</div>
                  <div className="text-sm text-gray-600 mt-1">Total Categories</div>
                </div>
              </div>

              <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>💡 Tip:</strong> Complete data ensures better candidate matching and streamlined recruitment. Review the missing information alerts in the Overview tab to improve your requirement quality.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Download Format Modal */}
        {showDownloadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
              <div className="bg-gradient-to-r from-brand-blue to-indigo-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">Choose Download Format</h3>
                <p className="text-blue-100 text-sm mt-1">
                  {showDownloadModal === 'recruiter' && 'Recruiter Guide'}
                  {showDownloadModal === 'screening' && 'Screening Questions'}
                  {showDownloadModal === 'both' && 'All Documents'}
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => {
                      if (showDownloadModal === 'recruiter') {
                        downloadDocument('docx');
                      } else if (showDownloadModal === 'screening') {
                        downloadQuestions('docx');
                      } else {
                        downloadDocument('docx');
                        setTimeout(() => downloadQuestions('docx'), 500);
                      }
                    }}
                    className="flex flex-col items-center gap-3 p-6 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                  >
                    <div className="text-5xl">📄</div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900 group-hover:text-blue-600">Word Document</div>
                      <div className="text-xs text-gray-600 mt-1">.DOCX format</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      if (showDownloadModal === 'recruiter') {
                        downloadDocument('pdf');
                      } else if (showDownloadModal === 'screening') {
                        downloadQuestions('pdf');
                      } else {
                        downloadDocument('pdf');
                        setTimeout(() => downloadQuestions('pdf'), 500);
                      }
                    }}
                    className="flex flex-col items-center gap-3 p-6 border-2 border-red-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all group"
                  >
                    <div className="text-5xl">📕</div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900 group-hover:text-red-600">PDF Document</div>
                      <div className="text-xs text-gray-600 mt-1">.PDF format</div>
                    </div>
                  </button>
                </div>
                <button
                  onClick={() => setShowDownloadModal(null)}
                  className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default RequirementResultsPage;